import axios, { isAxiosError } from 'axios';
import useAsync from 'Hooks/useAsync';
import { appConfigStore } from 'Models/AppConfigStore';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import moment from 'moment/moment';
import * as queryString from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { BOOKING_SUCCESS_PAGE_TITLE } from 'ConstantPageNames';
import { ROUTES, WLIDS } from 'Constants';
import { BookingEntity, GiftCertificateUsageEntity, RefundEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { themeStore } from 'Models/ThemeStore';
import { fetchBookingsForCart } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { fetchGiftCertificateUsagesByTransactionId } from 'Util/_HumanWritten/FetchGiftCertificates';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import ClassBookingSuccessSummary from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/ClassBookingSummary/ClassBookingSuccessSummary';
import TripBookingSummaryInner from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBookingSummaryInner';
import { CartCalculationLineItem } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/CartLineItems/CartCalculationLineItem';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { ParkingReservationSummaryCard } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/ParkingReservationSummaryCard';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import {
	getParkingReservationsByTransactionId,
	ParkingReservationSummaryDto,
} from 'Services/Api/_HumanWritten/ParkingService';
import { SteeringWheelSpinner } from 'Views/Components/_HumanWritten/Lottie/SteeringWheelSpinner';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import NavigationWrapper from 'Views/Components/Navigation/NavigationWrapper';
import BookingSuccessPromoCard from '../BookingPromoCard/BookingSuccessPromoCard';
import { resendConfirmationEmailModal } from '../ResendConfirmation/ResendConfirmationEmailModal';
import { handleForbiddenRequest } from 'Modules/Auth/authUtils';
import { transactionPricingQueryOptions } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { useQueries } from '@tanstack/react-query';
import { trackPurchase } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import {
	bookingsToItems,
	bookingsTotalValue,
	flattenBookingLegs,
} from 'Util/_HumanWritten/Analytics/analyticsBookingFields';
import { markPurchaseTracked, purchaseAlreadyTracked } from 'Util/_HumanWritten/Analytics/purchaseDedupe';

export interface BookingSuccessProps {}

export function BookingSuccessOuterWrapper() {
	const navigate = useNavigate();
	const intl = useIntl();

	useEffect(() => {
		document.title = BOOKING_SUCCESS_PAGE_TITLE;
	}, []);

	// Parse the query params from the page route
	const location = useLocation();
	const queryParams = queryString.parse(location.search.substring(1));
	// Pay later splits a cart into one transaction per booking, passed here as `?ids=a,b,c`.
	// A normal single-transaction checkout still uses `?id=`.
	const idsParam = queryParams.ids?.toString();
	const singleId = queryParams.id?.toString();
	const transactionIds = idsParam
		? idsParam
				.split(',')
				.map(s => s.trim())
				.filter(Boolean)
		: [singleId].filter((x): x is string => !!x);
	// Primary transaction — drives the secondary fetches (refunds / gift certs / parking, all
	// empty for pay later), analytics, resend, and the summary's transaction reference.
	const transactionId = transactionIds[0] ?? '';

	// Each transaction's own priced total — the same figure its summary card shows, and the only one that
	// carries transaction-level money: service fees, the card surcharge, vouchers and discounts. The
	// combined total below is built from these rather than from the bookings' summary totals, which hold
	// only per-booking amounts (see `grandTotal`).
	//
	// The same query the summary prices each transaction with, so the two share one request. Started
	// here, on mount, so it runs alongside fetch-bookings-for-cart rather than after it.
	const transactionPricing = useQueries({
		queries: transactionIds.map(id => transactionPricingQueryOptions(id, true, false)),
	});
	const pricedTotals = transactionPricing.map(x => x.data?.total ?? null);
	// Any transaction we could not price (or have not yet) leaves the combined total on its fallback
	// rather than silently under-reporting it.
	const transactionTotals =
		pricedTotals.length > 0 && pricedTotals.every((x): x is number => x !== null) ? pricedTotals : null;

	const [refunds, setRefunds] = useState<RefundEntity[]>();
	const [giftCertificateUsages, setGiftCertificateUsages] = useState<GiftCertificateUsageEntity[]>([]);
	const [redeemText, setRedeemText] = useState<string>(appConfigStore.voucherRedeemText);
	const [parkingReservations, setParkingReservations] = useState<ParkingReservationSummaryDto[]>([]);

	const response = useAsync(
		async abortSignal => {
			try {
				// Pay later splits a cart into one transaction per booking. Keep each transaction's
				// bookings grouped (not flattened) so each renders as its own summary with its own
				// correct total — a single flattened list would re-pair return legs into one card and
				// only ever total the first transaction.
				const groups = await Promise.all(
					transactionIds.map(async id => ({
						transactionId: id,
						bookings: await fetchBookingsForCart(id, true, abortSignal),
					})),
				);
				return groups;
			} catch (e) {
				if (isAxiosError(e) && e.response?.status === 403) {
					handleForbiddenRequest();
				}
				throw e;
			}
		},
		[idsParam, transactionId],
	);

	useEffect(() => {
		if (transactionId !== '') {
			const allowed = appConfigStore.allowedRefundMethodsFor(store.isStaff);
			if (allowed.has('Credit')) {
				fetchGiftCertificateUsagesByTransactionId(transactionId ?? '')
					.then(usages => {
						setGiftCertificateUsages(usages.data);
					})
					.catch(_ => {
						setGiftCertificateUsages([]);
					});
			}
			if (allowed.has('Bank')) {
				fetchRefunds(transactionId).then(res => {
					setRefunds(res);
				});
			}

			if (appConfigStore.parkingEnabled) {
				getParkingReservationsByTransactionId(transactionId)
					.then(res => setParkingReservations(res))
					.catch(() => setParkingReservations([]));
			}
		}
	}, [transactionId]);

	// Fire the `purchase` analytics event once per transaction, once the bookings have loaded.
	// This is the key conversion event; the operator's GTM container maps it to GA4 purchase,
	// Google Ads conversions, Meta Purchase, etc. See analyticsEvents.ts for the dataLayer contract.
	//
	// The ref guards against a repeat within this mount; `purchaseDedupe` guards against the
	// confirmation page being refreshed, navigated back to, or re-opened from the confirmation
	// email, all of which remount the page with the same transaction id and previously pushed a
	// second `purchase`.
	const purchaseTrackedRef = useRef<string | null>(null);
	useEffect(() => {
		if (response.type !== 'data') {
			return;
		}
		const groups = response.data;
		if (isNullOrUndefined(groups) || groups.length === 0 || stringIsEmpty(transactionId)) {
			return;
		}
		if (purchaseTrackedRef.current === transactionId || purchaseAlreadyTracked(transactionId)) {
			return;
		}
		purchaseTrackedRef.current = transactionId;
		markPurchaseTracked(transactionId);

		// Flatten every transaction's bookings (and their nested return legs) so the purchase
		// event reports the whole cart's value/items, not just the first transaction.
		const allBookings = flattenBookingLegs(groups.flatMap(g => g.bookings));
		trackPurchase({
			transaction_id: transactionId,
			value: bookingsTotalValue(allBookings),
			items: bookingsToItems(allBookings),
		});
	}, [response, transactionId]);

	if (response.type === 'loading') {
		return <SteeringWheelSpinner />;
	}

	if (response.type === 'error') {
		if (isAxiosError(response.error)) {
			if (response.error.response?.status === 403) {
				return <Navigate to="/404" replace />;
			}
		}
	}

	const bookingGroups = response.data;

	const zeroUsageGiftCertificates = giftCertificateUsages.filter(x => x.usedValue === 0)[0];

	if (isNullOrUndefined(bookingGroups) || bookingGroups.length === 0) {
		return <></>;
	}

	// Flat view of all bookings across every transaction, for the page-level checks below
	// (invoiced state, promo card, resend-email). Each transaction is still rendered separately.
	const bookingList = bookingGroups.flatMap(g => g.bookings);

	if (bookingList.length === 0) {
		return <></>;
	}

	// Combined total across every transaction (pay later splits a cart into several). Client-side only.
	//
	// Sum the TRANSACTIONS, not the bookings. A booking's summary total is per-booking by construction —
	// it carries the fare and the fees attached to that booking's own alteration, and nothing that lives
	// on the transaction: an automatically-applied service fee, the card surcharge, a gift voucher, a
	// transaction discount. Summing booking totals therefore reported a combined figure with the service
	// fee and the voucher missing, disagreeing with the per-transaction card right beside it. Falls back
	// to the old sum only while the pricing is in flight or if it could not be fetched.
	const grandTotal =
		transactionTotals !== null
			? transactionTotals.reduce((sum, x) => sum + x, 0)
			: bookingsTotalValue(flattenBookingLegs(bookingList));

	// GST portion of the (GST-inclusive) combined total, so the single total card can show "GST
	// included" + the GST amount just like a normal transaction's total card does.
	const gstPercentage = whiteLabelStore.config?.gstPercentage ?? 0;
	const combinedGst = gstPercentage > 0 ? grandTotal - grandTotal / (1 + gstPercentage / 100) : 0;

	const isInvoiced = bookingList.some(x => x.transactionStatus === 'INVOICED' || x.transactionStatus === 'AWAITING');
	const hideInvoiceDownload = !appConfigStore.invoicesForInvoicedUsers();

	// Resolve the configurable header/invoice strings up front so we can skip rendering
	// them entirely when an operator has blanked them out in the language settings
	// (e.g. set to a single space).
	const heroTitle = intl
		.formatMessage({
			id: 'booking-success__title',
			defaultMessage: 'Paradise awaits',
		})
		.trim();
	const heroSubtitle = intl
		.formatMessage({
			id: 'booking-success__subtitle',
			defaultMessage: 'Your trip is successfully booked.',
		})
		.trim();
	const taxInvoiceMessage = intl
		.formatMessage(
			!isInvoiced || store.isStaff || (isInvoiced && !hideInvoiceDownload)
				? {
						id: 'booking-success__tax-invoice-info',
						defaultMessage: 'Your tax invoice will be sent to you with your booking confirmation email.',
					}
				: {
						id: 'booking-wizard__no-tax-invoice__message',
						defaultMessage:
							'You will be issued an invoice from our accounting system in accordance ' +
							'with your credit terms.',
					},
		)
		.trim();

	const resendConfirmationEmail = () => {
		const bookingIds = Array.from(
			new Set(
				bookingList.flatMap(booking => {
					const ids: string[] = [booking.id];
					if (booking.returnBooking?.id) {
						ids.push(booking.returnBooking.id);
					}
					if (booking.returnBookingForId) {
						ids.push(booking.returnBookingForId);
					}
					return ids;
				}),
			),
		);

		const [first] = bookingList;

		resendConfirmationEmailModal(bookingIds, first.user.email, transactionId);
	};

	const onCreateAccount = () => {
		navigate(ROUTES.CONVERT_GUEST, {
			state: {
				firstName: store.userData?.firstName,
				lastName: store.userData?.lastName,
				email: store.userData?.email,
				phone: store.userData?.phone,
				postcode: store.userData?.postcode,
			},
		});
	};

	return (
		<div className="body-content booking-success">
			<img className="background-img" src={themeStore.bookingSuccessBackgroundImageURL} alt="bg-img" />
			<div className="colour-overlay" />
			<NavigationWrapper className="booking-success__nav" />
			<div className="booking-success__content">
				<div className="content__header">
					<If condition={stringNotEmpty(heroTitle)}>
						<h2>{heroTitle}</h2>
					</If>
					<If condition={stringNotEmpty(heroSubtitle)}>
						<h5>{heroSubtitle}</h5>
					</If>
					<div className="button-anchor">
						<Button
							display={Display.Solid}
							colors={Colors.Secondary}
							onClick={resendConfirmationEmail}
							newStyles
							buttonProps={{ 'data-test-id': 'booking-success-resend-email-button' } as any}
						>
							<Icon name="mail" />
							<FormattedMessage
								id={WLIDS.resendBookingConfirmation.initiator}
								defaultMessage="Resend confirmation email"
							/>
						</Button>
					</div>
				</div>
				<div className="content__body">
					{/* Sits directly below the header's Resend button, which keeps its anchored spot
					    overhanging the hero band. In normal flow (rather than stacked into the anchor)
					    so it pushes the summary down instead of overlapping it. */}
					{appConfigStore.guestBookingsEnabled && store.userData?.isGuest && (
						<div className="body__create-account">
							<Button
								display={Display.Solid}
								colors={Colors.Primary}
								onClick={onCreateAccount}
								newStyles
								buttonProps={{ 'data-test-id': 'booking-success-create-account-button' } as any}
							>
								<FormattedMessage {...WLIDS.actions.createAccount} />
							</Button>
						</div>
					)}
					<If condition={stringNotEmpty(taxInvoiceMessage)}>
						<p className="body__digital-ferry-ticket body__tax-invoice">{taxInvoiceMessage}</p>
					</If>
					<If condition={isBookingCreation(bookingList[0]) || isBookingAlteration(bookingList[0])}>
						<BookingSuccessPromoCard />
					</If>

					{/* Refund display logic - using same pattern as BookingCancelSuccess */}
					<BookingSuccessRefundSection
						refunds={refunds}
						giftCertificateUsage={zeroUsageGiftCertificates}
						redeemText={redeemText}
					/>

					{/* Sites with fare types get the grouped summary (design 5a / 8b). It replaces this whole
					    block, heading included, and prices each transaction itself. TEMPORARY: every other
					    site is back on the block below for now; designs 13b / 13c move them across by
					    dropping the `passengerClassesEnabled` check. */}
					{appConfigStore.passengerClassesEnabled &&
					bookingList.every(x => x.bookingSummaries?.recentSummary?.freightDetail == null) ? (
						<ClassBookingSuccessSummary bookingGroups={bookingGroups} hideLinks={store.userData?.isGuest} />
					) : (
						<>
							<h4 className="body__title">Booking summary:</h4>
							<div className="trip-summary-container">
								{/* Parent classname is required to inherit styles for trip card */}
								{bookingGroups.length === 1 ? (
									/* Single transaction: render normally, with its own full total card. */
									<TripBookingSummaryInner
										key={bookingGroups[0].transactionId}
										bookings={bookingGroups[0].bookings}
										refresh={undefined}
										tripSummaryLocation="booking-success"
										transactionId={bookingGroups[0].transactionId}
										showQrCodes
										hideLinks={store.userData?.isGuest}
										parkingReservations={parkingReservations}
									/>
								) : (
									/* Pay later splits a cart into a transaction per booking. Lay them out as one
							   summary — all cards in a single list with one combined total card top-right —
							   while each card keeps its own transaction's pricing (rendered cards-only). */
									<div className="trip-booking-summary-card__container cart-view">
										<div className="bookings-list__scrollable">
											{bookingGroups.map(group => (
												<TripBookingSummaryInner
													key={group.transactionId}
													bookings={group.bookings}
													refresh={undefined}
													tripSummaryLocation="booking-success"
													transactionId={group.transactionId}
													showQrCodes
													hideLinks={store.userData?.isGuest}
													parkingReservations={parkingReservations}
													cardsOnly
												/>
											))}
										</div>
										<div className="total-booking-price__container">
											<div className="booking-total-price__card">
												<div className="booking-total-price__card-inner">
													<div className="booking-total-price__breakdown">
														<CartCalculationLineItem
															keyToUse="grand-total-line-item"
															lineItemDisplayName="TOTAL"
															lineItemDescription={
																gstPercentage > 0
																	? `${appConfigStore.gstLabel} included`
																	: undefined
															}
															lineItemPrice={formatPriceDisplay(grandTotal, false)}
															lineItemSubPrice={
																combinedGst > 0 && gstPercentage > 0
																	? formatPriceDisplay(combinedGst, false, true)
																	: undefined
															}
															className="total-cart-line booking-success__combined-total"
														/>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

// New component to handle refund display logic
interface BookingSuccessRefundSectionProps {
	refunds?: RefundEntity[];
	giftCertificateUsage?: GiftCertificateUsageEntity;
	redeemText?: any;
}

function BookingSuccessRefundSection({ refunds, giftCertificateUsage, redeemText }: BookingSuccessRefundSectionProps) {
	// Show gift certificate if one was issued
	if (giftCertificateUsage) {
		return (
			<div className="gift-voucher-section">
				<h4>
					<FormattedMessage
						id="booking-success__credit-title"
						defaultMessage="Your account has been credited."
					/>
				</h4>
				<div className="credit-voucher">
					<h4 className="gift-voucher-voucher-number">
						Ferry voucher #{giftCertificateUsage?.giftCertificate?.code ?? 'Unknown'}
					</h4>
					<h3 className="gift-voucher-credited-amount">
						{formatCurrency(giftCertificateUsage?.giftCertificate?.initialValue)}
					</h3>
					<p className="gift-voucher-expiry-date">
						Expires: {moment(giftCertificateUsage?.giftCertificate?.expiryDate).format('DD MMM YYYY')}
					</p>
				</div>
				{redeemText && (
					<div
						className="redeem-text-section wysiwyg-content"
						dangerouslySetInnerHTML={{ __html: redeemText }}
					/>
				)}
			</div>
		);
	}

	// Show bank refunds if any were issued
	if (isNotNullOrUndefined(refunds) && refunds.length > 0) {
		const refundComponents = [];

		// Manual refunds
		const manualRefunds = refunds.filter(x => stringIsEmpty(x.providerRefundId));
		if (manualRefunds.length > 0) {
			const manualAmount = manualRefunds.reduce((sum, refund) => sum + refund.amount, 0);
			refundComponents.push(<RefundBox key="manual" amount={manualAmount} />);
		}

		// Provider refunds
		const providerRefunds = refunds.filter(x => stringNotEmpty(x.providerRefundId));
		if (providerRefunds.length > 0) {
			const providerRefundAmount = providerRefunds.reduce((sum, refund) => sum + refund.amount, 0);
			refundComponents.push(<ProviderRefundBox key="stripe" amount={providerRefundAmount} />);
		}

		return <>{refundComponents}</>;
	}

	// No refunds to display
	return null;
}

// Manual refund component
function RefundBox({ amount }: { amount: number }) {
	return (
		<div className="gift-voucher-section">
			<div className="credit-voucher">
				<h3 className="gift-voucher-voucher-number">
					<FormattedMessage id="booking-success__refund-card__title" defaultMessage="Refund amount" />
				</h3>
				<h2 className="gift-voucher-credited-amount">{formatCurrency(amount)}</h2>
				<p className="gift-voucher-expiry-date">
					<FormattedMessage
						id="booking-success__refund-card__info"
						defaultMessage="Staff will contact you to issue your refund in the next few days."
					/>
				</p>
			</div>
		</div>
	);
}

// Provider refund component
function ProviderRefundBox({ amount }: { amount: number }) {
	return (
		<div className="gift-voucher-section">
			<div className="credit-voucher">
				<h3 className="gift-voucher-voucher-number">
					<FormattedMessage id="booking-success__refund-card__title" defaultMessage="Refund amount" />
				</h3>
				<h2 className="gift-voucher-credited-amount">{formatCurrency(amount)}</h2>
				<p className="gift-voucher-expiry-date">
					<FormattedMessage
						id="booking-success__provider-refund-card__info"
						defaultMessage="Refund will take 5 - 10 business days to reach your account."
					/>
				</p>
			</div>
		</div>
	);
}

function isBookingCreation(booking: BookingEntity) {
	return appConfigStore.bookingSuccessPromoVisibleOnCreation && booking.alterations && booking.alterations.length < 2;
}

function isBookingAlteration(booking: BookingEntity) {
	return (
		appConfigStore.bookingSuccessPromoVisibleOnAlteration && booking.alterations && booking.alterations.length > 1
	);
}

async function fetchRefunds(transactionId: string) {
	const response = await axios.get<RefundEntity[]>(`/api/entity/RefundEntity/for-transaction/${transactionId}`);
	return (response.data ?? []).map(row => new RefundEntity(row));
}
